import React, { useEffect } from 'react';
import {
  isSameMonth,
  CalendarDate,
  DateField,
  isSameYear,
  today,
} from '@internationalized/date';
import { usePress } from 'react-aria/usePress';
import { focusWithoutScrolling } from 'react-aria/private/utils/focusWithoutScrolling';
import { mergeProps } from 'react-aria/mergeProps';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import { CalendarState } from './useCalendarState';
import { CalendarButton } from './CalendarButtons';

const isSame = { month: isSameMonth, year: isSameYear } as const;

export function CalendarMonthOrYearCell<T extends string>({
  date,
  state,
  isDisabled,
  field,
  children,
}: {
  date: CalendarDate;
  field: Extract<DateField, 'month' | 'year'>;
  state: CalendarState<T>;
  isDisabled?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const ref = React.useRef(null);
  const isFocused = state.focusedDate[field] === date[field];

  let tabIndex: number | undefined;
  if (!isDisabled) {
    tabIndex = isSame[field](date, state.focusedDate) ? 0 : -1;
  }

  useEffect(() => {
    if (isFocused && ref.current) {
      focusWithoutScrolling(ref.current);
    }
  }, [isFocused, ref]);

  const { pressProps } = usePress({
    isDisabled: state.isDisabled || isDisabled,
    onPress: () => {
      if (state.granularity === field) {
        state.setValue(date);
      } else {
        state.setFocusedDate(date);
      }

      if (!state.isReadOnly) {
        state.narrowView();
      }
    },
  });
  const buttonProps = mergeProps(pressProps, {
    onFocus() {
      if (!isDisabled) {
        state.setFocusedDate(date);
      }
    },
    tabIndex,
    role: 'button',
    'aria-disabled': isDisabled || undefined,
  });

  const isSelected = !!state.value && isSame[field](state.value, date);

  return (
    <td
      className={styles['sapphire-calendar__table-body-cell']}
      role="gridcell"
      aria-selected={isSelected}
    >
      <CalendarButton
        ref={ref}
        {...buttonProps}
        isInGridCell
        isSelected={isSelected}
        isDisabled={state.isDisabled || isDisabled}
        hasCurrentIndicator={isSame[field](date, today(state.timeZone))}
      >
        {children}
      </CalendarButton>
    </td>
  );
}
